import { HttpClient } from '@adapters/HttpClient';
import {
  CreateReceptionRequest,
  IdentifyPackageRequest,
  PackageInput,
  Reception,
  ReceptionFilters,
  ReceptionListItem,
  ReceptionPage,
  ReceptionsApiResponse,
  SplitPackageRequest,
  UpdateReceptionRequest,
} from '../models/Reception';

/**
 * Adapter para comunicación con la API de Recepciones
 * 
 * Maneja todas las llamadas HTTP relacionadas con recepciones y paquetes.
 */
export class ReceptionApiAdapter {
  private readonly basePath = '/receptions';

  constructor(private httpClient: HttpClient) {}

  // ============================================================================
  // RECEPCIONES - CRUD
  // ============================================================================

  /**
   * Obtiene lista paginada de recepciones
   */
  async getReceptions(filters: ReceptionFilters = {}): Promise<ReceptionPage> {
    const {
      status,
      date_from,
      date_to,
      carrier,
      code,
      per_page = 25,
      page = 1,
      sort_column = 'created_at',
      sort_direction = 'desc',
    } = filters;

    // Construir parámetros de consulta
    const params: Record<string, any> = {
      page,
      per_page,
      sort_column,
      sort_direction,
    };

    // Agregar filtros opcionales
    if (status) params.status = status;
    if (date_from) params.date_from = date_from;
    if (date_to) params.date_to = date_to;
    if (carrier) params.carrier = carrier;
    if (code) params.code = code;

    // Mostrar loading global solo en primera página
    const showGlobalLoading = page === 1;

    const response = await this.httpClient.get<ReceptionsApiResponse>(
      this.basePath,
      params,
      {},
      showGlobalLoading
    );

    return {
      receptions: response.data,
      nextPage:
        response.meta.current_page < response.meta.last_page
          ? response.meta.current_page + 1
          : undefined,
      hasMore: response.meta.current_page < response.meta.last_page,
      total: response.meta.total,
    };
  }

  /**
   * Obtiene todas las recepciones (sin paginación)
   */
  async getAllReceptions(filters: ReceptionFilters = {}): Promise<ReceptionListItem[]> {
    const response = await this.getReceptions({ ...filters, per_page: 100 });
    return response.receptions;
  }

  /**
   * Obtiene el detalle de una recepción con todos sus paquetes
   */
  async getReception(id: number): Promise<Reception> {
    const response = await this.httpClient.get<Reception>(
      `${this.basePath}/${id}`,
      {},
      {},
      true
    );
    return response;
  }

  /**
   * Crea una nueva recepción.
   * Si data.signature_file está presente, envía multipart/form-data con la firma en JPG.
   * Si no, envía JSON con signature_ref (legacy).
   */
  async createReception(data: CreateReceptionRequest): Promise<Reception> {
    if (data.signature_file) {
      const formData = new FormData();
      formData.append('carrier', data.carrier);
      formData.append('vehicle_plate', data.vehicle_plate);
      formData.append('driver_name', data.driver_name);
      // Enviar packages como array según especificación API (como en el cURL de ejemplo)
      data.packages.forEach((pkg: PackageInput, index: number) => {
        const prefix = `packages[${index}]`;
        formData.append(`${prefix}[package_type]`, pkg.package_type);
        formData.append(`${prefix}[quantity]`, String(pkg.quantity));
        if (pkg.package_type_other != null && pkg.package_type_other !== '') {
          formData.append(`${prefix}[package_type_other]`, pkg.package_type_other);
        }
        formData.append(`${prefix}[incident_type]`, pkg.incident_type ?? '');
        formData.append(`${prefix}[image_ref]`, pkg.image_ref ?? '');
      });
      // API espera el archivo de firma con el nombre de campo 'signature' (según cURL)
      formData.append('signature', data.signature_file, data.signature_file.name);

      const response = await this.httpClient.post<Reception>(
        this.basePath,
        formData,
        true
      );
      return response;
    }

    const response = await this.httpClient.post<Reception>(
      this.basePath,
      data,
      true
    );
    return response;
  }

  /**
   * Actualiza una recepción existente
   */
  async updateReception(id: number, data: UpdateReceptionRequest): Promise<Reception> {
    const response = await this.httpClient.put<Reception>(
      `${this.basePath}/${id}`,
      data
    );
    return response;
  }

  /**
   * Elimina una recepción
   */
  async deleteReception(id: number): Promise<void> {
    await this.httpClient.delete(`${this.basePath}/${id}`);
  }

  // ============================================================================
  // PAQUETES - OPERACIONES
  // ============================================================================

  /**
   * Identifica un paquete asignándole un destino y payload
   */
  async identifyPackage(
    receptionId: number,
    packageId: number,
    data: IdentifyPackageRequest
  ): Promise<Reception> {
    const response = await this.httpClient.post<{ reception: Reception; actions: Record<string, any> }>(
      `${this.basePath}/${receptionId}/packages/${packageId}/identify`,
      data,
      true
    );
    // La API devuelve { reception, actions }, extraemos solo la recepción
    return response.reception;
  }

  /**
   * Divide un paquete en dos
   */
  async splitPackage(
    receptionId: number,
    packageId: number,
    data: SplitPackageRequest
  ): Promise<Reception> {
    const response = await this.httpClient.post<Reception>(
      `${this.basePath}/${receptionId}/packages/${packageId}/split`,
      data,
      true
    );
    return response;
  }
}

